import * as React from 'react';
import { Composition } from 'remotion';
import {
  DEFAULT_BRAND,
  type BannerRatio,
} from '@proptr/creative-templates';
import {
  ListingVideo,
  type ListingVideoProps,
  photosFor,
  videoDurationFrames,
  videoSize,
} from './ListingVideo.js';

// Remotion's entry point. `npm run studio -w @proptr/video` opens these
// for editing; the API renders the same ids headlessly.
//
// One composition per ratio rather than one with a size prop: Remotion
// resolves dimensions at registration, and the studio's preview needs the
// canvas up front.

export const FPS = 30;

// Placeholder used only by the studio. Renders pass real props through
// `inputProps`, which override these entirely.
const PREVIEW_PROPS: Omit<ListingVideoProps, 'ratio'> = {
  brand: { ...DEFAULT_BRAND, radius: 0 },
  listing: {
    imageUrl:
      'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?auto=format&fit=crop&w=1200&q=70',
    gallery: null,
    imageAlt: '',
    badge: 'FOR SALE',
    locality: 'Dubai Marina',
    city: 'Dubai',
    price: 2850000,
    currency: 'AED',
    locale: 'en-AE',
    beds: '2',
    baths: '3',
    area: null,
    areaUnit: 'sqft',
    permitQr: null,
  },
};

const RATIOS: { id: string; ratio: BannerRatio }[] = [
  { id: 'listing-square', ratio: '1:1' },
  { id: 'listing-vertical', ratio: '9:16' },
];

export const RemotionRoot: React.FC = () => (
  <>
    {RATIOS.map(({ id, ratio }) => {
      const { width, height } = videoSize(ratio);
      return (
        <Composition
          key={id}
          id={id}
          component={ListingVideo}
          // Length follows how many photos the listing actually has, so a
          // one-photo listing doesn't sit on a frozen frame for the tail
          // of a clip sized for four.
          durationInFrames={videoDurationFrames(1, FPS)}
          calculateMetadata={({ props }) => ({
            durationInFrames: videoDurationFrames(
              photosFor(props.listing, props.ratio).length,
              FPS,
            ),
          })}
          fps={FPS}
          width={width}
          height={height}
          defaultProps={{ ...PREVIEW_PROPS, ratio }}
        />
      );
    })}
  </>
);
